import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { CheckCircle, X, MessageSquare, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const AdminApprovalsPage = () => {
  const { api } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    try {
      const response = await api.get('/approvals/pending');
      setApprovals(response.data);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (decision) => {
    if (!decision) return { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' };
    if (decision === 'approved') return { label: 'Approved', color: 'bg-green-500/20 text-green-400' };
    return { label: 'Changes Requested', color: 'bg-red-500/20 text-red-400' };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">Approvals Center</h1>
          <p className="text-fp-textMuted text-sm mt-1">{approvals.length} pending approvals</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {approvals.map((approval) => {
            const status = getStatusBadge(approval.decision);
            return (
              <div
                key={approval.id}
                className="bg-fp-surface border border-white/5 rounded-sm overflow-hidden hover:border-primary/30 transition-colors"
                data-testid={`approval-card-${approval.id}`}
              >
                {approval.deliverable && (
                  <>
                    <div className="h-40 bg-fp-bg flex items-center justify-center">
                      <Eye className="w-8 h-8 text-fp-textMuted" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs rounded ${status.color}`}>
                          {status.label}
                        </span>
                        <span className="px-2 py-1 bg-white/5 text-fp-textMuted text-xs rounded">
                          {approval.deliverable.type}
                        </span>
                      </div>
                      <h3 className="font-semibold text-white mb-2">{approval.deliverable.title}</h3>
                      <p className="text-fp-textMuted text-sm mb-4">
                        Requested: {new Date(approval.requested_at).toLocaleDateString()}
                      </p>
                      <Button
                        onClick={() => {
                          setSelectedApproval(approval);
                          setDetailOpen(true);
                        }}
                        className="w-full btn-secondary py-2"
                      >
                        View Details
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {approvals.length === 0 && !loading && (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">All caught up!</h3>
            <p className="text-fp-textMuted">No pending approvals at the moment.</p>
          </div>
        )}

        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Approval Details</DialogTitle>
            </DialogHeader>
            {selectedApproval?.deliverable && (
              <div className="space-y-4">
                <div className="p-4 bg-fp-bg rounded-sm">
                  <h3 className="font-semibold text-white mb-2">{selectedApproval.deliverable.title}</h3>
                  <p className="text-fp-textMuted text-sm">{selectedApproval.deliverable.caption}</p>
                </div>
                {selectedApproval.comments?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-2">Comments</h4>
                    <div className="space-y-2">
                      {selectedApproval.comments.map((comment) => (
                        <div key={comment.id} className="p-3 bg-fp-bg rounded-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-primary text-sm font-medium">{comment.author}</span>
                            <span className="text-fp-textMuted text-xs">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-white text-sm">{comment.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminApprovalsPage;
